import {
  Component,
  Input,
  Output,
  EventEmitter,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FrozenColumnsService } from '@helpers/table/frozen-columns.service';
import { ButtonComponent } from '@view/shared/molecules/button/button.component';
import {
  ButtonTypeEnum,
  ButtonSizeEnum
} from '@view/shared/molecules/button/buttonTypesEnum';
import { ToastService } from '@view/shared/molecules/toast/service/toast.service';
import { ToastTypeEnum } from '@view/shared/molecules/toast/toast-type-enum';

export interface ColumnOption {
  key: string;
  label: string;
}

/**
 * Sidebar component for selecting frozen (pinned) columns
 *
 * Features:
 * - Checkbox list of available columns
 * - Maximum 5 columns selection
 * - Immediate application (no Apply button needed)
 * - Counter display showing frozen columns count
 * - Clear all button to reset frozen columns
 *
 * @story HU04-898440 - Inmovilizar Columnas
 */
@Component({
  selector: 'app-frozen-columns-sidebar',
  standalone: true,
  imports: [CommonModule, ButtonComponent],
  templateUrl: './frozen-columns-sidebar.component.html'
})
export class FrozenColumnsSidebarComponent {
  // Expose enums to template
  readonly ButtonTypeEnum = ButtonTypeEnum;
  readonly ButtonSizeEnum = ButtonSizeEnum;

  /**
   * Available columns to freeze
   */
  @Input() columns: ColumnOption[] = [];

  /**
   * Control sidebar visibility
   */
  @Input() isOpen = false;

  /**
   * Event emitted when sidebar should be closed
   */
  @Output() closeSidebar = new EventEmitter<void>();

  /**
   * Computed frozen columns count
   */
  frozenCount!: ReturnType<typeof computed<number>>;

  /**
   * Computed max limit
   */
  maxColumns!: number;

  /**
   * Computed is limit reached
   */
  readonly isLimitReached = computed(() =>
    this.frozenColumnsService.isLimitReached()
  );

  constructor(
    public readonly frozenColumnsService: FrozenColumnsService,
    private readonly toastService: ToastService
  ) {
    // Initialize computed properties
    this.frozenCount = computed(() =>
      this.frozenColumnsService.frozenColumnsCount()
    );
    this.maxColumns = this.frozenColumnsService.getMaxFrozenColumns();
  }

  /**
   * Check if a column is frozen
   */
  isColumnFrozen(columnKey: string): boolean {
    return this.frozenColumnsService.isColumnFrozen(columnKey);
  }

  /**
   * Check if a column checkbox should be disabled
   * (when limit reached and column is not currently frozen)
   */
  isColumnDisabled(columnKey: string): boolean {
    return this.isLimitReached() && !this.isColumnFrozen(columnKey);
  }

  /**
   * Handle checkbox change
   */
  onColumnToggle(columnKey: string): void {
    const wasAlreadyFrozen = this.isColumnFrozen(columnKey);
    const success = this.frozenColumnsService.toggleColumn(columnKey);
    const currentCount = this.frozenCount();

    // Show toast when selecting the 5th column (reaching the limit)
    if (success && !wasAlreadyFrozen && currentCount === this.maxColumns) {
      this.toastService.setToast({
        title: `Máximo ${this.maxColumns} columnas pueden ser inmovilizadas`,
        type: ToastTypeEnum.WARNING
      });

      setTimeout(() => {
        this.toastService.setToast(null);
      }, 10000);
    }
  }

  /**
   * Clear all frozen columns
   */
  onClearAll(): void {
    this.frozenColumnsService.clearAll();
  }

  /**
   * Close sidebar
   */
  onClose(): void {
    this.closeSidebar.emit();
  }

  /**
   * Handle backdrop click to close sidebar
   */
  onBackdropClick(): void {
    this.onClose();
  }

  /**
   * Prevent click propagation on sidebar content
   */
  onSidebarClick(event: Event): void {
    event.stopPropagation();
  }
}
